// Right-size images at request time.
//
// Two kinds of source live in the catalogue:
//
//   - Local, generated by scripts/prepare_works.py. These carry an `imageSet`
//     of pre-rendered widths; we build a srcset from it and pick the default
//     to match the slot.
//   - Remote Unsplash URLs, which may still exist in an admin's browser-local
//     catalogue from before the real photographs went in. Unsplash honours
//     `w=` and `auto=format`, so we ask for the width the slot needs rather
//     than the 1200px the seed used to store.
//
// Anything else (admin uploads are compressed `data:` URIs) passes through.

const UNSPLASH = 'https://images.unsplash.com/';

function isRemote(url) {
  return typeof url === 'string' && url.startsWith(UNSPLASH);
}

export function sized(url, width, quality = 70) {
  if (!isRemote(url)) return url;
  try {
    const u = new URL(url);
    u.searchParams.set('auto', 'format');
    u.searchParams.set('fit', 'crop');
    u.searchParams.set('w', String(Math.round(width)));
    u.searchParams.set('q', String(quality));
    return u.toString();
  } catch {
    return url;
  }
}

export function srcSet(url, widths, quality = 70) {
  if (!isRemote(url)) return undefined;
  return widths.map((w) => `${sized(url, w, quality)} ${w}w`).join(', ');
}

/**
 * The one call components should make. Returns `{ src, srcSet, width, height }`
 * for a work's main image (or, with `field = 'beforeImage'`, its before image),
 * whichever kind of source it is. `width`/`height` are only known for local
 * sets — pass them to <img> so the box is reserved before the bytes arrive.
 */
export function picture(work, field, widths, fallbackWidth) {
  const url = work && work[field];
  const set = work && (field === 'image' ? work.imageSet : work.beforeSet);

  if (Array.isArray(set) && set.length) {
    const pick =
      set.filter((r) => r.w <= fallbackWidth).sort((a, b) => b.w - a.w)[0] || set[set.length - 1];
    return {
      src: pick.webp,
      srcSet: set.map((r) => `${r.webp} ${r.w}w`).join(', '),
      width: pick.w,
      height: pick.h,
    };
  }
  return {
    src: sized(url, fallbackWidth),
    srcSet: srcSet(url, widths),
    width: undefined,
    height: undefined,
  };
}

// A shelf card is 220px on phones and 268px from `sm` up; 2x displays want
// double that, so 480 and 768 cover it without a 1024 download.
export const CARD_WIDTHS = [320, 480, 640, 800];
export const CARD_SIZES = '(min-width: 640px) 268px, 220px';

// Expanded grid: 2 / 3 / 4 columns inside a 1216px container.
export const GRID_SIZES = '(min-width: 1024px) 22vw, (min-width: 640px) 30vw, 45vw';

// Lightbox and the before/after stage fill most of the viewport.
export const FULL_WIDTHS = [640, 960, 1280, 1600];
export const FULL_SIZES = '(min-width: 1024px) 70vw, 92vw';
